import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import { formatCurrency } from '../utils';
import Modal from '../components/Modal';
import { Btn } from '../components/Input';
import { IconEdit, IconTrash, IconPlus, IconUsers, IconSearch } from '../components/Icons';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import { SkeletonRow } from '../components/Skeleton';
import EmptyState from '../components/EmptyState';
import KlientForm from '../components/KlientForm';

// ─── Karta klienta (mobile) ──────────────────────────────────────────────────

function KlientCard({ k, onOpen, onEdit, onDelete, t }) {
  const headlineName = k.nazwa_firmy || k.imie_nazwisko || k.nazwa;
  const subName = k.nazwa_firmy && k.imie_nazwisko ? k.imie_nazwisko : null;
  return (
    <div className="ds-card" style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div className="flex items-start justify-between gap-2">
        <button
          onClick={onOpen}
          className="text-left hover:underline"
          style={{ flex: 1, minWidth: 0, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {headlineName}
          </div>
          {subName && (
            <div style={{ fontSize: '10px', color: 'var(--ink2)', marginTop: '1px' }}>{subName}</div>
          )}
        </button>
        <div className="flex gap-1">
          <button onClick={onEdit}
            className="w-8 h-8 flex items-center justify-center rounded-btn"
            style={{ background: 'var(--bg4)', color: 'var(--ink3)', border: '1px solid var(--border)' }}
            aria-label={t('common.edit')}>
            <IconEdit className="w-4 h-4" />
          </button>
          <button onClick={onDelete}
            className="w-8 h-8 flex items-center justify-center rounded-btn"
            style={{ background: 'var(--bg4)', color: 'var(--err2)', border: '1px solid var(--border)' }}
            aria-label={t('common.delete')}>
            <IconTrash className="w-4 h-4" />
          </button>
        </div>
      </div>

      {(k.email || k.telefon || k.nip) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', borderTop: '1px solid var(--border)', paddingTop: '8px' }}>
          {k.email && <div style={{ fontSize: '11px', color: 'var(--ink2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{k.email}</div>}
          {k.telefon && <div style={{ fontSize: '11px', color: 'var(--ink2)' }}>{k.telefon}</div>}
          {k.nip && <div style={{ fontSize: '10px', color: 'var(--ink3)', fontFamily: 'monospace' }}>NIP: {k.nip}</div>}
        </div>
      )}

      <div className="flex items-center justify-between" style={{ borderTop: '1px solid var(--border)', paddingTop: '8px' }}>
        <span style={{ fontSize: '11px', color: 'var(--ink2)' }}>
          <span style={{ color: 'var(--ink)', fontWeight: 700 }}>{k.liczba_spacerow || 0}</span> {t('clients.spaces').toLowerCase()}
        </span>
        <span
          style={{
            fontSize: '12px',
            fontWeight: 700,
            color: k.kwota_narosla > 0 ? 'var(--acc2)' : 'var(--ink3)',
            fontFamily: 'monospace',
          }}>
          {formatCurrency(k.kwota_narosla)}
        </span>
      </div>
    </div>
  );
}

// ─── Lista klientów ───────────────────────────────────────────────────────────

export default function Klienci({ onNavigate, initialAction, onActionConsumed }) {
  const { t } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();
  const [klienci, setKlienci]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(null);
  const [search, setSearch]     = useState('');

  const load = () => {
    setLoading(true);
    api.getKlienci().then(setKlienci).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // Konsumuj initialAction (np. z onboardingu Dashboard) — otwórz modal raz.
  useEffect(() => {
    if (initialAction === 'add') {
      setModal('add');
      onActionConsumed?.();
    }
  }, [initialAction, onActionConsumed]);

  const handleAdd  = async form => {
    const result = await api.addKlient(form);
    toast.success(t('toast.added'));
    if (result?.id) {
      onNavigate('klient', result.id);
    } else {
      load();
    }
  };
  const handleEdit = async form => {
    await api.updateKlient(modal.edit.id, form);
    load();
    toast.success(t('toast.saved'));
  };
  const handleDelete = async k => {
    const ok = await confirm({
      title: t('confirmDialog.deleteTitle'),
      message: t('clients.deleteConfirm', { name: k.nazwa }),
      confirmLabel: t('common.delete'),
      cancelLabel: t('common.cancel'),
      danger: true,
    });
    if (!ok) return;
    try {
      await api.deleteKlient(k.id);
      toast.success(t('toast.deleted'));
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const filtered = klienci.filter(k => {
    const q = search.toLowerCase();
    return (
      (k.nazwa || '').toLowerCase().includes(q) ||
      (k.imie_nazwisko || '').toLowerCase().includes(q) ||
      (k.nazwa_firmy || '').toLowerCase().includes(q) ||
      (k.email || '').toLowerCase().includes(q) ||
      (k.telefon || '').includes(q) ||
      (k.nip || '').includes(q)
    );
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 style={{ fontSize: '18px', fontWeight: 800, color: 'var(--ink)' }}>{t('clients.title')}</h1>
        <Btn onClick={() => setModal('add')}>
          <span className="flex items-center gap-1.5"><IconPlus className="w-3.5 h-3.5" /> {t('clients.add')}</span>
        </Btn>
      </div>

      <div className="mb-4">
        <input
          className="ds-input w-full sm:max-w-[280px]"
          placeholder={t('clients.searchPlaceholder')}
          value={search} onChange={e => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="ds-card overflow-hidden">
          {Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} cols={5} />)}
        </div>
      ) : filtered.length === 0 ? (
        search ? (
          <EmptyState
            icon={<IconSearch className="w-12 h-12" />}
            title={t('common.noResults')}
            description={t('clients.searchNoResultsHint', { query: search })}
            action={<Btn variant="ghost" onClick={() => setSearch('')}>{t('clients.clearSearch')}</Btn>}
          />
        ) : (
          <EmptyState
            icon={<IconUsers className="w-12 h-12" />}
            title={t('clients.noClients')}
            description={t('clients.noClientsHint')}
            action={
              <Btn onClick={() => setModal('add')}>
                <span className="flex items-center gap-1.5"><IconPlus className="w-3.5 h-3.5" /> {t('clients.addFirst')}</span>
              </Btn>
            }
          />
        )
      ) : (
        <>
          {/* Mobile: lista kart (<md) */}
          <div className="md:hidden flex flex-col gap-2.5">
            {filtered.map(k => (
              <KlientCard
                key={k.id}
                k={k}
                onOpen={() => onNavigate('klient', k.id)}
                onEdit={() => setModal({ edit: k })}
                onDelete={() => handleDelete(k)}
                t={t}
              />
            ))}
          </div>

          {/* Desktop: tabela (md+) */}
          <div className="ds-card overflow-hidden hidden md:block">
            <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)', padding: '10px 16px', textAlign: 'left' }}>{t('clients.client')}</th>
                  <th style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)', padding: '10px 16px', textAlign: 'left' }}>{t('clients.contact')}</th>
                  <th style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)', padding: '10px 16px', textAlign: 'left' }} className="hidden lg:table-cell">NIP</th>
                  <th style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)', padding: '10px 16px', textAlign: 'center' }}>{t('clients.spaces')}</th>
                  <th style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)', padding: '10px 16px', textAlign: 'right' }}>{t('clients.debt')}</th>
                  <th style={{ padding: '10px 16px' }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(k => (
                  <tr key={k.id} className="transition-colors" style={{ borderBottom: '1px solid var(--border)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <td className="px-4 py-3">
                      <button
                        style={{ fontSize: '12px', fontWeight: 600, color: 'var(--ink)' }}
                        className="text-left block hover:underline"
                        onClick={() => onNavigate('klient', k.id)}>
                        {k.nazwa_firmy || k.imie_nazwisko || k.nazwa}
                      </button>
                      {k.nazwa_firmy && k.imie_nazwisko && (
                        <span style={{ fontSize: '10px', color: 'var(--ink2)' }}>{k.imie_nazwisko}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {k.email && <div style={{ fontSize: '11px', color: 'var(--ink2)' }}>{k.email}</div>}
                      {k.telefon && <div style={{ fontSize: '11px', color: 'var(--ink2)' }}>{k.telefon}</div>}
                      {!k.email && !k.telefon && <span style={{ fontSize: '11px', color: 'var(--ink3)' }}>—</span>}
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      {k.nip
                        ? <div style={{ fontSize: '11px', color: 'var(--ink2)', fontFamily: 'monospace' }}>{k.nip}</div>
                        : <span style={{ fontSize: '11px', color: 'var(--ink3)' }}>—</span>
                      }
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span style={{ fontSize: '11px', color: 'var(--ink)' }}>{k.liczba_spacerow || 0}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {k.kwota_narosla > 0
                        ? <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--acc2)', fontFamily: 'monospace' }}>{formatCurrency(k.kwota_narosla)}</span>
                        : <span style={{ fontSize: '11px', color: 'var(--ink3)', fontFamily: 'monospace' }}>{formatCurrency(k.kwota_narosla)}</span>
                      }
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => setModal({ edit: k })}
                          className="w-7 h-7 flex items-center justify-center rounded-btn transition-colors"
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--bg4)'}
                          onMouseLeave={e => e.currentTarget.style.background = ''}
                          style={{ color: 'var(--ink3)' }} title={t('common.edit')}>
                          <IconEdit className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(k)}
                          className="w-7 h-7 flex items-center justify-center rounded-btn transition-colors"
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--bg4)'}
                          onMouseLeave={e => e.currentTarget.style.background = ''}
                          style={{ color: 'var(--err2)' }} title={t('common.delete')}>
                          <IconTrash className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        </>
      )}

      {modal === 'add' && (
        <Modal title={t('clients.add')} onClose={() => setModal(null)} wide>
          <KlientForm onSave={handleAdd} onClose={() => setModal(null)} />
        </Modal>
      )}
      {modal?.edit && (
        <Modal title={t('clients.editClient')} onClose={() => setModal(null)} wide>
          <KlientForm initial={modal.edit} onSave={handleEdit} onClose={() => setModal(null)} />
        </Modal>
      )}
    </div>
  );
}
